import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Mail, Lock, User, AlertCircle, Eye, EyeOff, Check, Crown, Zap } from 'lucide-react';
import { db } from '../firebase/config';
import { doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { EMOJIS } from '../constants/emojis';

export function Signup() {
  const [searchParams] = useSearchParams();
  const inviteId = searchParams.get('invite');
  const inviteEmail = searchParams.get('email');

  const [name, setName] = useState('');
  const [email, setEmail] = useState(inviteEmail || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<'free' | 'premium'>('free');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [inviteInfo, setInviteInfo] = useState<any>(null);
  
  const { signup } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Load invitation details if invite ID is provided
    if (inviteId) {
      loadInviteInfo();
    }
  }, [inviteId]);

  async function loadInviteInfo() {
    try {
      const inviteDoc = await getDoc(doc(db, 'invites', inviteId!));
      if (inviteDoc.exists()) {
        const invite = inviteDoc.data();
        setInviteInfo({ id: inviteDoc.id, ...invite });
        
        // Get household name
        const householdDoc = await getDoc(doc(db, 'households', invite.householdId));
        if (householdDoc.exists()) {
          setInviteInfo((prev: any) => ({
            ...prev,
            householdName: householdDoc.data().name
          }));
        }
      }
    } catch (err) {
      console.error('Error loading invite:', err);
    }
  }

  async function acceptInvitation() {
    if (!inviteId || !inviteInfo) return;

    try {
      // Use Cloud Function to accept invitation (bypasses Firestore rules)
      const functions = getFunctions();
      const acceptInvitationFn = httpsCallable(functions, 'acceptInvitation');
      
      const result = await acceptInvitationFn({ inviteId });
      const data = result.data as any;

      if (!data.success) {
        throw new Error(data.message || 'Failed to join household');
      }
    } catch (err: any) {
      console.error('Error accepting invitation:', err);
      throw new Error(err.message || 'Failed to join household');
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    // Validation
    if (password.length < 6) {
      return setError('Password must be at least 6 characters');
    }

    if (password !== confirmPassword) {
      return setError('Passwords do not match');
    }

    // If there's an invite, verify email matches
    if (inviteInfo && email !== inviteInfo.email) {
      return setError(`Please use the invited email address: ${inviteInfo.email}`);
    }

    try {
      setError('');
      setLoading(true);
      
      // Sign up the user with selected plan (skip household creation if accepting invitation)
      await signup(email, password, name, !!inviteId, selectedPlan);
      
      // If there's an invitation, accept it using Cloud Function
      if (inviteId && inviteInfo) {
        await acceptInvitation();
      }

      // If user selected premium, redirect to payment
      if (selectedPlan === 'premium' && !inviteId) {
        window.location.href = 'https://buy.stripe.com/test_eVq5kC9pD56jgXhdDJ9AA00';
      } else {
        navigate('/dashboard', { replace: true });
      }
    } catch (error: any) {
      console.error('Signup error:', error);
      setError(error.message || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-mint/30 via-neutral-bg to-primary-mint/20 flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <img src="/logo.png" alt="FridgeLife" className="w-20 h-20 mx-auto mb-3" />
          </Link>
          <h1 className="text-3xl font-bold text-primary-green">
            {inviteInfo ? 'Join Household' : 'Create Account'}
          </h1>
          <p className="text-gray-600 mt-2">
            {inviteInfo 
              ? `Join ${inviteInfo.householdName} and start reducing food waste`
              : 'Start reducing food waste today'
            }
          </p>
        </div>

        {/* Invitation Notice */}
        {inviteInfo && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
            <p className="text-sm text-blue-900">
              <strong>{EMOJIS.ALERT} You've been invited!</strong><br />
              Create your account to join <strong>{inviteInfo.householdName}</strong>
            </p>
          </div>
        )}

        {/* Plan Selection - Only show if not accepting invitation */}
        {!inviteInfo && (
          <div className="mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4 text-center">Choose Your Plan</h2>
            <div className="grid grid-cols-2 gap-4">
              {/* Free Plan */}
              <button
                type="button"
                onClick={() => setSelectedPlan('free')}
                className={`relative p-5 rounded-xl border-2 transition-all text-left ${
                  selectedPlan === 'free'
                    ? 'border-primary-green bg-primary-mint/10 shadow-lg'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                {selectedPlan === 'free' && (
                  <div className="absolute -top-2 -right-2 w-6 h-6 bg-primary-green rounded-full flex items-center justify-center">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                )}
                <div className="flex items-center gap-2 mb-2">
                  <Zap className="w-5 h-5 text-primary-green" />
                  <h3 className="font-bold text-gray-900">Free Trial</h3>
                </div>
                <p className="text-2xl font-bold text-gray-900 mb-2">$0</p>
                <p className="text-xs text-gray-600 mb-3">30-day trial</p>
                <ul className="space-y-1.5 text-xs text-gray-700">
                  <li className="flex items-start gap-1.5">
                    <Check className="w-3.5 h-3.5 text-primary-green flex-shrink-0 mt-0.5" />
                    <span>15 items max</span>
                  </li>
                  <li className="flex items-start gap-1.5">
                    <Check className="w-3.5 h-3.5 text-primary-green flex-shrink-0 mt-0.5" />
                    <span>2 members</span>
                  </li>
                  <li className="flex items-start gap-1.5">
                    <Check className="w-3.5 h-3.5 text-primary-green flex-shrink-0 mt-0.5" />
                    <span>5 barcode scans/month</span>
                  </li>
                  <li className="flex items-start gap-1.5">
                    <Check className="w-3.5 h-3.5 text-primary-green flex-shrink-0 mt-0.5" />
                    <span>Basic analytics</span>
                  </li>
                </ul>
              </button>

              {/* Premium Plan */}
              <button
                type="button"
                onClick={() => setSelectedPlan('premium')}
                className={`relative p-5 rounded-xl border-2 transition-all text-left ${
                  selectedPlan === 'premium'
                    ? 'border-yellow-500 bg-gradient-to-br from-yellow-50 to-orange-50 shadow-lg'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                {selectedPlan === 'premium' && (
                  <div className="absolute -top-2 -right-2 w-6 h-6 bg-yellow-500 rounded-full flex items-center justify-center">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                )}
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-yellow-400 text-yellow-900 text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm whitespace-nowrap">
                    BEST VALUE
                  </span>
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <Crown className="w-5 h-5 text-yellow-600" />
                  <h3 className="font-bold text-gray-900">Premium</h3>
                </div>
                <div className="flex items-baseline gap-1 mb-2">
                  <p className="text-2xl font-bold text-gray-900">$4</p>
                  <p className="text-sm text-gray-600">/month</p>
                </div>
                <p className="text-xs text-gray-600 mb-3">All features unlocked</p>
                <ul className="space-y-1.5 text-xs text-gray-700">
                  <li className="flex items-start gap-1.5">
                    <Check className="w-3.5 h-3.5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <span className="font-semibold">Unlimited items</span>
                  </li>
                  <li className="flex items-start gap-1.5">
                    <Check className="w-3.5 h-3.5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <span className="font-semibold">Unlimited members</span>
                  </li>
                  <li className="flex items-start gap-1.5">
                    <Check className="w-3.5 h-3.5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <span className="font-semibold">Unlimited scans</span>
                  </li>
                  <li className="flex items-start gap-1.5">
                    <Check className="w-3.5 h-3.5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <span className="font-semibold">Advanced analytics</span>
                  </li>
                </ul>
              </button>
            </div>
            {selectedPlan === 'premium' && (
              <p className="text-xs text-center text-gray-500 mt-3">
                You'll be redirected to complete payment after creating your account
              </p>
            )}
          </div>
        )}

        {/* Signup Form */}
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
          {error && (
            <div className="bg-red-50 text-red-800 p-4 rounded-xl mb-6 flex items-start gap-3 border border-red-200">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <p className="text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Name
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-green focus:border-primary-green transition-all"
                  placeholder="John Doe"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={!!inviteEmail}
                  className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-green focus:border-primary-green transition-all disabled:bg-gray-50 disabled:text-gray-500"
                  placeholder="you@example.com"
                />
              </div>
              {inviteEmail && (
                <p className="text-xs text-gray-500 mt-2">
                  Using invited email address
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full pl-11 pr-12 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-green focus:border-primary-green transition-all"
                  placeholder="At least 6 characters"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Must be at least 6 characters long
              </p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="w-full pl-11 pr-12 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-green focus:border-primary-green transition-all"
                  placeholder="Re-enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
              {confirmPassword && password !== confirmPassword && (
                <p className="text-xs text-red-600 mt-2 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  Passwords do not match
                </p>
              )}
              {confirmPassword && password === confirmPassword && (
                <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                  {EMOJIS.CHECKMARK} Passwords match
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary-green text-white py-3.5 rounded-xl font-semibold hover:bg-primary-green/90 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-primary-green/30 transition-all hover:shadow-xl"
            >
              {loading ? 'Creating your account...' : inviteInfo ? 'Create Account & Join' : 'Create Account'}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-center text-sm text-gray-600">
              Already have an account?{' '}
              <Link 
                to={inviteId ? `/login?invite=${inviteId}` : '/login'} 
                className="text-primary-green font-semibold hover:underline"
              >
                Log in
              </Link>
            </p>
          </div>
        </div>

        <p className="text-center text-xs text-gray-500 mt-6">
          By signing up, you agree to save food and help the planet {EMOJIS.PLANT}
        </p>
      </div>
    </div>
  );
}
